export interface MapHandoffQuery {
  state: string
  challenge: string
  projectId?: number
  customerHint?: 'silver-bow'
  contractVersion?: 1
  client?: 'hosted' | 'local-development'
}

const proofPattern = /^[A-Za-z0-9_-]{43}$/
const allowedKeys = new Set([
  'state',
  'challenge',
  'project_id',
  'customer_id_hint',
  'contract_version',
  'client',
])

function validOptionalProject(project: unknown): boolean {
  if (project === undefined) return true
  return (
    typeof project === 'string' &&
    /^[1-9]\d*$/.test(project) &&
    Number.isSafeInteger(Number(project))
  )
}

function validOptionalScope(
  project: unknown,
  hint: unknown,
  version: unknown,
  client: unknown,
): boolean {
  return (
    validOptionalProject(project) &&
    [undefined, 'silver-bow'].includes(hint as string | undefined) &&
    [undefined, '1'].includes(version as string | undefined) &&
    [undefined, 'hosted', 'local-development'].includes(client as string | undefined) &&
    (client !== 'local-development' || version === '1')
  )
}

export function parseMapHandoffQuery(query: Record<string, unknown>): MapHandoffQuery | null {
  if (Object.keys(query).some((key) => !allowedKeys.has(key))) return null
  const {
    state,
    challenge,
    project_id: project,
    customer_id_hint: hint,
    contract_version: version,
    client,
  } = query
  if (
    typeof state !== 'string' ||
    typeof challenge !== 'string' ||
    !proofPattern.test(state) ||
    !proofPattern.test(challenge)
  )
    return null
  if (!validOptionalScope(project, hint, version, client)) return null
  const result: MapHandoffQuery = { state, challenge }
  if (client !== undefined) result.client = client as MapHandoffQuery['client']
  if (project !== undefined) result.projectId = Number(project)
  if (hint === 'silver-bow') result.customerHint = hint
  if (version !== undefined || project !== undefined || hint !== undefined)
    result.contractVersion = 1
  return result
}

export function mapLoginResumePath(query: MapHandoffQuery): string {
  const params = new URLSearchParams({ state: query.state, challenge: query.challenge })
  if (query.projectId) params.set('project_id', String(query.projectId))
  if (query.customerHint) params.set('customer_id_hint', query.customerHint)
  if (query.contractVersion) params.set('contract_version', String(query.contractVersion))
  if (query.client) params.set('client', query.client)
  return '/map-login?' + params.toString()
}

export function validatedMapResume(value: unknown, projectId: number): string | null {
  if (
    typeof value !== 'string' ||
    !value.startsWith('/map-login?') ||
    value.includes('#') ||
    value.includes('\\')
  )
    return null
  const url = new URL(value, 'https://platform.invalid')
  const params = Array.from(url.searchParams.entries())
  if (new Set(params.map(([key]) => key)).size !== params.length) return null
  const parsed = parseMapHandoffQuery(Object.fromEntries(params))
  return parsed?.contractVersion === 1 && parsed.projectId === projectId
    ? mapLoginResumePath(parsed)
    : null
}

export function registeredMapOrigin(
  environment: string,
  client: MapHandoffQuery['client'] = 'hosted',
): string | null {
  if (client === 'local-development')
    return environment === 'production' ? 'http://localhost:5199' : null
  const origins: Record<string, string> = {
    local: 'http://localhost:5174',
    integration: 'https://silverbowmaps.int.onmoria.com',
    production: 'https://silverbowmaps.onmoria.com',
  }
  return origins[environment] ?? null
}

export function validatedMapViewerUrl(
  value: string,
  projectId: number,
  origin: string | null,
): string | null {
  try {
    const url = new URL(value)
    if (
      !origin ||
      url.origin !== origin ||
      url.username ||
      url.password ||
      !Number.isSafeInteger(projectId) ||
      projectId <= 0
    )
      return null
    url.searchParams.set('project_id', String(projectId))
    return url.href
  } catch {
    return null
  }
}

export function validatedMapCallback(value: string, state: string, origin: string | null): string {
  const target = new URL(value)
  const fragment = new URLSearchParams(target.hash.slice(1))
  if (
    !origin ||
    target.origin !== origin ||
    target.pathname !== '/auth/platform/callback' ||
    target.search ||
    target.username ||
    target.password ||
    fragment.get('state') !== state ||
    !proofPattern.test(fragment.get('code') ?? '') ||
    Array.from(fragment).length !== 2
  ) {
    throw new Error('Map sign-in could not be opened. Reopen the map from the workbook.')
  }
  return target.href
}
